<template>
  <div class="form-container">
    <div class="form-header">
      <h2>{{ isEditing ? 'Edit User' : 'Create New User' }}</h2>
    </div>
    <LoadingIndicator v-if="isLoading">Loading user...</LoadingIndicator>
    <form v-else @submit.prevent="handleSubmit" class="form">
      <div class="form-section">
        <h3>Personal Information</h3>
        <div class="form-group half-width">
          <label for="first_name" class="required">First Name</label>
          <input
            type="text"
            id="first_name"
            v-model.trim="formData.first_name"
            class="form-control"
            maxlength="80"
            required
            :disabled="!isSuperuser()"
          />
        </div>

        <div class="form-group half-width">
          <label for="last_name" class="required">Last Name</label>
          <input
            type="text"
            id="last_name"
            v-model.trim="formData.last_name"
            class="form-control"
            maxlength="80"
            required
            :disabled="!isSuperuser()"
          />
        </div>

        <div class="form-group half-width">
          <label for="email" class="required">Email</label>
          <input
            type="email"
            id="email"
            v-model.trim="formData.email"
            class="form-control"
            maxlength="120"
            required
            :disabled="!isSuperuser()"
          />
        </div>

        <div class="form-group half-width" v-if="!isEditing">
          <label for="password" class="required">Password</label>
          <input
            type="password"
            id="password"
            v-model="formData.password"
            class="form-control"
            placeholder="**********"
            required
          />
        </div>

        <div class="form-group half-width">
          <label for="phone">Phone</label>
          <input
            type="tel"
            id="phone"
            v-model.trim="formData.phone"
            class="form-control"
            maxlength="20"
          />
        </div>

        <div class="form-group half-width" v-if="isEditing && userId == 'me' && !verifiedEmail">
          <div class="success-message" v-if="verifEmailResendSuccess">
            Verification email has been sent!
          </div>
          <div class="error-message" v-else-if="verifEmailResendError">
            {{ verifEmailResendError }}
          </div>
          <div v-else>
            Your email address has not been verified.<br>Please check your inbox for the verification link.<br>
            <span class="link" @click="handleResendVerification" :disabled="verifEmailResendLoading">
            {{ verifEmailResendLoading ? 'Sending...' : 'Send a new verification email' }}
            </span>
          </div>
        </div>

        <div class="form-group half-width" v-if="isEditing && userId == 'me'">
          <router-link to="/changepassword" class="link">Change Password</router-link>
        </div>
      </div>

      <div class="form-section" v-if="isSuperuser()">
        <h3>Contact & Company Details</h3>
        <div class="form-group third-width">
          <label for="role">Role / Title</label>
          <input
            type="text"
            id="role"
            v-model.trim="formData.role"
            class="form-control"
            maxlength="80"
          />
        </div>

        <div class="form-group two-thirds-width">
          <label for="company">Organization</label>
          <input
            type="text"
            id="company"
            v-model.trim="formData.company"
            class="form-control"
            maxlength="80"
          />
        </div>

        <div class="form-group third-width">
          <label for="phone">Phone</label>
          <input
            type="tel"
            id="phone"
            v-model.trim="formData.phone"
            class="form-control"
            maxlength="20"
          />
        </div>

        <div class="form-group two-thirds-width">
          <label for="website_url">Website</label>
          <input
            type="url"
            id="website_url"
            v-model.trim="formData.website_url"
            class="form-control"
            maxlength="255"
          />
        </div>
        
        <div class="form-group full-width">
          <label for="address_line_1">Address</label>
          <input
            type="text"
            id="address_line_1"
            v-model.trim="formData.address_line_1"
            class="form-control"
            placeholder="Address Line 1"
            maxlength="255"
          />
        </div>

        <div class="form-group full-width">
          <input
            type="text"
            id="address_line_2"
            v-model.trim="formData.address_line_2"
            class="form-control"
            placeholder="Address Line 2"
            maxlength="255"
          />
        </div>

        <div class="form-group half-width">
          <label for="city">City</label>
          <input
            type="text"
            id="city"
            v-model.trim="formData.city"
            class="form-control"
            maxlength="80"
          />
        </div>

        <div class="form-group half-width">
          <label for="state">State</label>
          <input
            type="text"
            id="state"
            v-model.trim="formData.state"
            class="form-control"
            maxlength="80"
          />
        </div>

        <div class="form-group half-width">
          <label for="country">Country</label>
          <input
            type="text"
            id="country"
            v-model.trim="formData.country"
            class="form-control"
            maxlength="80"
          />
        </div>

        <div class="form-group half-width">
          <label for="postal_code">Postal Code / ZIP</label>
          <input
            type="text"
            id="postal_code"
            v-model.trim="formData.postal_code"
            class="form-control"
            maxlength="20"
          />
        </div>
      </div>

      <div class="form-actions">
        <button type="submit" class="submit-button big">
          {{ isEditing ? 'Save Changes' : 'Create User' }}
        </button>
        <button @click="handleLogout" class="logout-button medium" v-if="userId == 'me'">
          <font-awesome-icon :icon="['fas', 'sign-out-alt']" />
          Logout
        </button>
        <button type="button" class="cancel-button medium" @click="handleCancel">
          Cancel
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { isSuperuser } from '@/scripts/permissions';
import { handleApiError } from '@/scripts/shared-utils';
import { logout, resendVerification } from '@/scripts/services/authService';
import { getUser, createUser, updateUser, userKeys } from '@/scripts/api/userApi';
import LoadingIndicator from '@/components/LoadingIndicator.vue';

const router = useRouter();
const route = useRoute();
const queryClient = useQueryClient();

const userId = computed(() => route.params.user_id);
const isEditing = computed(() => !!userId.value);

const formData = ref({});
const verifiedEmail = ref(false);
const verifEmailResendLoading = ref(false);
const verifEmailResendSuccess = ref(false);
const verifEmailResendError = ref('');

const userQueryKey = computed(() => userKeys.getUser(userId.value));

const { data: user, isLoading } = useQuery({
  queryKey: userQueryKey,
  queryFn: () => getUser(userId.value),
  enabled: isEditing,
  staleTime: 60000,
  onError: (error) => handleApiError(error, 'fetching user')
});

watch(user, (newUserData) => {
  if (newUserData) {
    formData.value = { ...newUserData };
    verifiedEmail.value = newUserData.verified_email;
  }
}, { immediate: true });

const { mutate: saveUser } = useMutation({
  mutationFn: (userData) => {
    if (isEditing.value) {
      return updateUser(userId.value, userData);
    }
    return createUser(userData);
  },
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: userKeys.getAllUsers() });
    if (isEditing.value) {
      queryClient.invalidateQueries({ queryKey: userKeys.getUser(userId.value) });
    }
    closeForm();
  },
  onError: (error) => {
    handleApiError(error, 'saving user');
    alert('Error saving user. Please try again.');
  },
});

const handleSubmit = () => {
  // explicitly set the fields we want to update (or create)
  const userData = {
    phone: formData.value.phone,
    website_url: formData.value.website_url,
    company: formData.value.company,
    role: formData.value.role,
    address_line_1: formData.value.address_line_1,
    address_line_2: formData.value.address_line_2,
    city: formData.value.city,
    state: formData.value.state,
    country: formData.value.country,
    postal_code: formData.value.postal_code,
  };

  if (!isEditing.value) {
    userData.password = formData.value.password;
  }

  if (!isEditing.value || isSuperuser()) {
    userData.first_name = formData.value.first_name;
    userData.last_name = formData.value.last_name;
    userData.email = formData.value.email;
  }

  saveUser(userData);
};

const handleLogout = () => {
  logout();
  router.push('/login');
};

const handleCancel = () => {
  router.back();
};

const closeForm = () => {
  router.push(isSuperuser() ? '/admin/users' : '/');
};

const handleResendVerification = async () => {
  if (verifEmailResendLoading.value) return;
  verifEmailResendLoading.value = true;
  verifEmailResendSuccess.value = false;
  verifEmailResendError.value = '';

  const result = await resendVerification();
  if (result === true) {
    verifEmailResendSuccess.value = true;
  } else {
    verifEmailResendError.value = result;
  }
  verifEmailResendLoading.value = false;
};
</script>

<style scoped>
@import '@/styles/shared-form.css';
</style>
